import { Entity, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Column } from '../../decorators';
import { OrderStatus } from '../../enums';
import { formatEnumAsSelectOptions } from '../../utils';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientItemEntity } from './client-item.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { MSmPaymentTransactionOrderEntity } from './m-sm-payment-transaction-order.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { OrderFileEntity } from './order-file.entity';
import { OrderNoteEntity } from './order-note.entity';
import { PublicUserEntity } from './public-user.entity';
import { ShippingMethodEntity } from './shipping-method.entity';
import { TaxEntity } from './tax.entity';
import { TripStopEntity } from './trip-stop.entity';
import { UserEntity } from './user.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'])
@Index('fk_public_user__id_override', ['fkPublicUserIdOverride'])
@Index('fk_public_user__id_submitter', ['fkPublicUserIdSubmitter'])
@Index('fk_tax__id', ['fkTaxId'])
@Index('fk_user__id_override', ['fkUserIdOverride'])
@Index('fk_user__id_submitter', ['fkUserIdSubmitter'])
@Index('fx_order_fulltext', ['externalId', 'customerPO'], { fulltext: true })
@Index('m_sm_payment_fk_m_payment_method__id', ['mSmPaymentFkMPaymentMethodId'])
@Index('order__fk_client__id_fk', ['fkClientId'])
@Index('order__fk_client_address__id_billing_fk', ['fkClientAddressIdBilling'])
@Index('order__fk_client_address__id_shipping_fk', [
	'fkClientAddressIdShipping',
])
@Index('order__fk_user__id_fk', ['fkUserId'])
@Index('order_externalId_fk_company__id__deleted_userId', [
	'externalId',
	'fkCompanyId',
	'deletedUserId',
])
@Index('idx_order_company_status_deleted_date', [
	'fkCompanyId',
	'deletedDate',
	'status',
	'date',
])
@Index('idx_order_deleted_company_date', ['deletedDate', 'fkCompanyId', 'date'])
@Index('idx_order_client_status_deleted', [
	'fkClientId',
	'status',
	'deletedDate',
])
@Entity('order')
export class OrderEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('datetime', { name: 'date', nullable: true })
	public date!: Date | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('int', { name: 'numberOfLines', nullable: true })
	public numberOfLines!: number | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column(
		'varchar',
		{
			name: 'status',
			nullable: true,
			length: 255,
			default: () => `'${OrderStatus.UNSUBMITTED}'`,
		},
		{
			selectOptions: formatEnumAsSelectOptions(OrderStatus),
		},
	)
	public status!: OrderStatus | null;

	@Column('varchar', { name: 'customerPO', nullable: true, length: 255 })
	public customerPO!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'alertStatus', nullable: true, length: 255 })
	public alertStatus!: string | null;

	@Column('tinyint', { name: 'canCancel', width: 1, default: () => "'0'" })
	public canCancel!: boolean;

	@Column('varchar', { name: 'origin', length: 255, default: () => "'2'" })
	public origin!: string;

	@Column('varchar', { name: 'shipVia', nullable: true, length: 255 })
	public shipVia!: string | null;

	@Column('text', { name: 'statusText', nullable: true })
	public statusText!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 255 })
	public externalId2!: string | null;

	@Column('date', { name: 'shippedByDate', nullable: true })
	public shippedByDate!: string | null;

	@Column('int', {
		name: 'fk_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdSubmitter!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_shipping',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdShipping!: number | null;

	@Column('date', { name: 'expirationDate', nullable: true })
	public expirationDate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdSubmitter!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@Column('longtext', { name: 'approvalRequiredReason', nullable: true })
	public approvalRequiredReason!: string | null;

	@Column('datetime', { name: 'overrideDateTime', nullable: true })
	public overrideDateTime!: Date | null;

	@Column('int', {
		name: 'fk_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdOverride!: number | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('int', {
		name: 'm_sm_payment_fk_m_payment_method__id',
		nullable: true,
		unsigned: true,
	})
	public mSmPaymentFkMPaymentMethodId!: number | null;

	@Column('varchar', {
		name: 'm_sm_payment_fee_unit',
		nullable: true,
		length: 32,
	})
	public mSmPaymentFeeUnit!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeAmount!: string;

	@Column('decimal', {
		name: 'shipCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public shipCost!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('int', { name: 'fk_tax__id', nullable: true, unsigned: true })
	public fkTaxId!: number | null;

	@Column('decimal', {
		name: 'taxRate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public taxRate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdOverride!: number | null;

	@Column('varchar', { name: 'jobNumber', nullable: true, length: 64 })
	public jobNumber!: string | null;

	@Column('varchar', { name: 'internalReference', nullable: true, length: 64 })
	public internalReference!: string | null;

	@Column('int', {
		name: 'fk_shipping_method__id',
		nullable: true,
		unsigned: true,
	})
	public fkShippingMethodId!: number | null;

	@Column('json', {
		name: 'appliedCouponCodes',
		nullable: true,
		comment:
			'Array of coupon codes entered against this cart/order. Persisted on the cart so codes survive page reloads and the promo engine sees them on every recompute. Null/empty array = no codes entered.',
	})
	public appliedCouponCodes!: Array<string> | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkOrderIdLast2, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkOrder, {})
	public clients2!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkOrderIdLast2,
		{},
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(
		() => ClientItemEntity,
		(clientItem) => clientItem.fkOrderIdLast2,
		{},
	)
	public clientItems!: Array<ClientItemEntity>;
	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkOrder, {})
	public clientItems2!: Array<ClientItemEntity>;

	@OneToMany(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.mSmFkOrder,
		{},
	)
	public mSmClientOpportunities!: Array<MSmClientOpportunityEntity>;

	@OneToMany(
		() => MSmPaymentTransactionOrderEntity,
		(mSmPaymentTransactionOrder) =>
			mSmPaymentTransactionOrder.mSmPaymentFkOrder,
		{},
	)
	public mSmPaymentTransactionOrders!: Array<MSmPaymentTransactionOrderEntity>;

	@ManyToOne(() => UserEntity, (user) => user.submittedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id_submitter', referencedColumnName: 'id' }])
	public submittedByUser!: UserEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_submitter', referencedColumnName: 'id' },
	])
	public submittedByPublicUser!: PublicUserEntity;

	@ManyToOne(() => CompanyEntity, (company) => company.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => UserEntity, (user) => user.overriddenOrders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_user__id_override', referencedColumnName: 'id' }])
	public fkUserIdOverride2!: UserEntity;

	@ManyToOne(
		() => MPaymentPaymentMethodEntity,
		(mPaymentPaymentMethod) => mPaymentPaymentMethod.orders,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_sm_payment_fk_m_payment_method__id',
			referencedColumnName: 'id',
		},
	])
	public mSmPaymentFkMPaymentMethod!: MPaymentPaymentMethodEntity;

	@ManyToOne(() => TaxEntity, (tax) => tax.orders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_tax__id', referencedColumnName: 'id' }])
	public fkTax!: TaxEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders2, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_override', referencedColumnName: 'id' },
	])
	public fkPublicUserIdOverride2!: PublicUserEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders2,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_shipping', referencedColumnName: 'id' },
	])
	public fkClientAddressIdShipping2!: ClientAddressEntity;

	@ManyToOne(() => ClientEntity, (client) => client.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => UserEntity, (user) => user.assignedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	@ManyToOne(
		() => ShippingMethodEntity,
		(shippingMethod) => shippingMethod.orders,
		{
			onDelete: 'SET NULL',
			onUpdate: 'CASCADE',
		},
	)
	@JoinColumn([{ name: 'fk_shipping_method__id', referencedColumnName: 'id' }])
	public shippingMethod!: ShippingMethodEntity;

	@OneToMany(() => OrderFileEntity, (orderFile) => orderFile.fkOrder, {})
	public orderFiles!: Array<OrderFileEntity>;

	@OneToMany(() => OrderDetailEntity, (orderDetail) => orderDetail.order, {})
	public orderDetails!: Array<OrderDetailEntity>;

	@OneToMany(() => OrderNoteEntity, (orderNote) => orderNote.fkOrder, {})
	public orderNotes!: Array<OrderNoteEntity>;

	// Distribution refactor: order ↔ trip_stop linkage.
	@Column('int', {
		name: 'fk_trip_stop__id',
		unsigned: true,
		nullable: true,
	})
	public fkTripStopId!: number | null;

	@ManyToOne(() => TripStopEntity, { onDelete: 'SET NULL' })
	@JoinColumn([{ name: 'fk_trip_stop__id', referencedColumnName: 'id' }])
	public fkTripStop?: TripStopEntity;
}
